import {useEffect, useState} from "react";

export type OtsAvatarSize = "sm" | "md" | "lg" | "xl";

export type OtsAvatarProps = {
  name?: string;
  src?: string;
  size?: OtsAvatarSize;
  /** Hide from assistive technology when the name is already read out beside the avatar. */
  decorative?: boolean;
  className?: string;
};

const sizeClasses: Record<OtsAvatarSize, string> = {
  sm: "size-8 text-xs",
  md: "size-10 text-sm",
  lg: "size-14 text-lg",
  xl: "size-20 text-2xl"
};

/** First and last initials, e.g. "Maria de la Torres" -> "MT". */
export const getInitials = (name?: string): string => {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }

  const first = parts[0].charAt(0);
  const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
  return (first + last).toUpperCase();
};

/** A person's photo, or their initials on the brand primary when there is no photo to show. */
export const OtsAvatar = ({
  name,
  src,
  size = "md",
  decorative = false,
  className
}: OtsAvatarProps) => {
  // A broken photo URL falls back to initials rather than the browser's broken-image glyph.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const showImage = Boolean(src) && !failed;

  return (
    <span
      role={decorative ? undefined : "img"}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
      className={[
        "inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full bg-[var(--ots-primary)] font-[family-name:var(--ots-font-display)] font-semibold text-[var(--ots-on-primary)]",
        sizeClasses[size],
        className
      ].filter(Boolean).join(" ")}>
      {showImage
        ? <img src={src} alt="" className="size-full object-cover" onError={() => setFailed(true)}/>
        : <span aria-hidden="true">{getInitials(name)}</span>}
    </span>
  );
};
